import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NavBar from "../../components/NavBar/NavBar";
import styles from "./DirectMessages.module.css";
import returnIcon from "../../assets/returnIcon.png";
import { BASE_URL } from "../../App";

function linkify(text) {
    if (!text) return "";
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, (url) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
}

function useDedupe() {
    const lastIdRef = useRef(0);
    const seenIdsRef = useRef(new Set());

    const seed = (existingMessages) => {
        const list = existingMessages || [];
        lastIdRef.current = list.length ? list[list.length - 1].id : 0;
        seenIdsRef.current = new Set(list.map((m) => m.id));
    };

    const mergeById = (setMessages, incoming) => {
        if (!incoming?.length) return;
        const fresh = [];
        for (const m of incoming) {
            if (!seenIdsRef.current.has(m.id)) {
                seenIdsRef.current.add(m.id);
                fresh.push(m);
                if (m.id > lastIdRef.current) lastIdRef.current = m.id;
            }
        }
        if (fresh.length) setMessages((prev) => [...prev, ...fresh]);
    };

    const getSinceQS = () =>
        lastIdRef.current ? `&since_id=${encodeURIComponent(lastIdRef.current)}` : "";

    return { seed, mergeById, getSinceQS, lastIdRef };
}

export default function DirectMessagePage() {
    const { threadId, userId } = useParams();
    const isNew = !threadId && !!userId;

    const navigate = useNavigate();
    const [otherUser, setOtherUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    const bottomRef = useRef(null);
    const { seed, mergeById, getSinceQS } = useDedupe();

    const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: "auto" });

    const [flash, setFlash] = useState({ type: "", text: "" });
    const flashTimerRef = useRef(null);
    const errTimerRef = useRef(null); // Ref for the error message timeout

    function showFlash(text, type = "error", ms = 5000) {
        setFlash({ type, text });
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlash({ type: "", text: "" }), ms);
    }

    useEffect(() => {
        return () => {
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
            if (errTimerRef.current) clearTimeout(errTimerRef.current); // Clear on unmount
        };
    }, []);

    // -------------------------------------------------------
    //                LOAD CONVERSATION
    // -------------------------------------------------------
    const loadConversation = async () => {
        if (isNew) {
            // New DM mode → get profile of other user
            const res = await fetch(
                `${BASE_URL}/backend/display_profile.php?user_id=${encodeURIComponent(userId)}`
            );
            const data = await res.json();

            if (!res.ok || !data.success || !data.profile) {
                throw new Error("Failed to load conversation. Please try again later");
            }

            setOtherUser({
                id: data.profile.user_id,
                // support BOTH teammate code and your username system
                name: data.profile.name ?? null,
                username: data.profile.username ?? null,
                avatar_path: data.profile.avatar_path,
            });

            setMessages([]);
            seed([]);
            return;
        }

        // Existing conversation mode
        const res = await fetch(
            `${BASE_URL}/backend/messages/get_conversation.php?conversation_id=${encodeURIComponent(
                threadId
            )}`,
            { credentials: "include" }
        );
        const data = await res.json();

        if (!res.ok || data.error)
            throw new Error("Failed to load conversation. Please try again later");

        // Merge teammate + your username
        setOtherUser({
            id: data.other_user.id,
            name: data.other_user.name ?? null,
            username: data.other_user.username ?? null,
            avatar_path: data.other_user.avatar_path,
        });

        setMessages(data.messages || []);
        seed(data.messages || []);

        // Mark messages read
        fetch(`${BASE_URL}/backend/messages/mark_read.php`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ conversation_id: Number(threadId) }),
        });
    };

    useEffect(() => {
        let alive = true;
        
        // Clear previous error timer
        if (errTimerRef.current) clearTimeout(errTimerRef.current);
        setErr(""); // Clear any previous error message

        (async () => {
            try {
                await loadConversation();
            } catch (e) {
                if (alive) {
                    const errorMsg = "Failed to load conversation. Please try again later";
                    setErr(errorMsg);
                    
                    // NEW: Set timeout to clear the error after 5000ms (5 seconds)
                    errTimerRef.current = setTimeout(() => {
                        setErr("");
                        errTimerRef.current = null;
                    }, 5000);
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
            // Also clear the error timer on effect cleanup
            if (errTimerRef.current) clearTimeout(errTimerRef.current);
        };
    }, [threadId, userId]);

    useLayoutEffect(scrollToBottom, [messages]);

    // -------------------------------------------------------
    //                    POLLING
    // -------------------------------------------------------
    useEffect(() => {
        if (!threadId) return;

        let alive = true;
        let controller = null;
        let timerId = null;
        let running = false;
        let visible = document.visibilityState === "visible";

        const clearPending = () => {
            if (timerId) clearTimeout(timerId);
            timerId = null;
            if (controller) controller.abort();
            controller = null;
            running = false;
        };

        const schedule = (ms) => {
            if (!alive || !visible) return;
            timerId = setTimeout(pollOnce, ms);
        };

        const pollOnce = async () => {
            if (!alive || running || !visible) return;
            running = true;

            controller = new AbortController();
            try {
                const url =
                    `${BASE_URL}/backend/messages/get_conversation.php` +
                    `?conversation_id=${encodeURIComponent(threadId)}` +
                    getSinceQS();

                const res = await fetch(url, {
                    credentials: "include",
                    signal: controller.signal,
                });
                const data = await res.json();

                if (res.ok && !data.error && Array.isArray(data.messages)) {
                    mergeById(setMessages, data.messages);
                }
            } catch {}
            finally {
                running = false;
                controller = null;
                if (alive && visible) schedule(700);
            }
        };

        const onVisibility = () => {
            const nowVisible = document.visibilityState === "visible";
            if (nowVisible === visible) return;
            visible = nowVisible;
            if (!visible) clearPending();
            else {
                clearPending();
                pollOnce();
            }
        };

        if (visible) pollOnce();
        document.addEventListener("visibilitychange", onVisibility);

        const stopAll = () => {
            visible = false;
            clearPending();
        };
        const resumeAll = () => {
            visible = true;
            clearPending();
            pollOnce();
        };

        window.addEventListener("pagehide", stopAll);
        window.addEventListener("pageshow", resumeAll);

        return () => {
            alive = false;
            clearPending();
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pagehide", stopAll);
            window.removeEventListener("pageshow", resumeAll);
        };
    }, [threadId, BASE_URL]);

    // -------------------------------------------------------
    //                     SEND MESSAGE
    // -------------------------------------------------------
    const send = async () => {
        const text = draft.trim();
        if (!text) return;

        if (text.length > 200) {
            showFlash("Message too long (max 200 chars).");
            return;
        }

        const payload = isNew
            ? { body: text, other_user_id: Number(userId) }
            : { body: text, conversation_id: Number(threadId) };

        try {
            const res = await fetch(`${BASE_URL}/backend/messages/send_message.php`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const data = await res.json();

            if (!res.ok || data.error) {
                showFlash("Error sending message. Please try again later");
                return;
            }

            setDraft("");
            mergeById(setMessages, [data.message]);
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });

            if (isNew && data.conversation_id) {
                navigate(`/messages/${data.conversation_id}`);
            }
        } catch (e) {
            showFlash("Error sending message. Please try again later");
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    // -------------------------------------------------------
    //                       RENDER
    // -------------------------------------------------------
    return (
        <div className={styles.pageWrap}>
            <div className={styles.navBarWrap}>
                <NavBar />
            </div>

            <div className={styles.threadBar}>
                <button className={styles.backBtn} onClick={() => navigate("/messages")}>
                    <img className={styles.backIcon} src={returnIcon} alt="" />
                </button>

                <div className={styles.threadInfo}>
                    <div
                        className={styles.threadName}
                        onClick={() => {
                            if (otherUser?.id) navigate(`/profile/${otherUser.id}`);
                        }}
                    >
                        {loading
                            ? "Direct Message"
                            : otherUser?.username
                            ? `@${otherUser.username}`
                            : otherUser?.name
                            ? otherUser.name
                            : "@unknown"}
                    </div>
                    <div className={styles.threadSub}>{otherUser?.subtitle || ""}</div>
                </div>
            </div>

            {flash.text && (
                <div className={flash.type === "error" ? styles.alertError : styles.alertSuccess}>
                    {flash.text}
                </div>
            )}

            <div className={styles.chatWrap}>
                <div className={styles.scroller}>
                    {loading && <div>Loading…</div>}
                    {err && <div className={styles.alertError}>{err}</div>} {/* Use {err} here */}

                    {messages.map((m) => (
                        <div key={m.id} className={m.is_me ? styles.rowMe : styles.rowThem}>
                            <div className={styles.bubble}>
    <div
        className={styles.msgBody}
        dangerouslySetInnerHTML={{ __html: linkify(m.body) }}
    ></div>

    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
        {new Date(m.created_at).toLocaleString()}
    </div>
</div>

                        </div>
                    ))}
                    <div ref={bottomRef} />
                </div>

                <div className={styles.inputBar}>
                    <textarea
                        className={styles.input}
                        placeholder="Type your message…"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={onKeyDown}
                        rows={1}
                    />
                    <button className={styles.sendBtn} onClick={send}>
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
}